import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAdminSettings, useAdminUpdateSetting } from "@/lib/admin";
import { describeError } from "@/lib/errors";
import {
  DEFAULT_LEGAL_PRIVACY,
  DEFAULT_LEGAL_TERMS,
  type LegalMarkdown,
} from "@/shared/legal-documents";

const Editor = lazy(() => import("@/components/legal-markdown-editor"));

type Locale = "de" | "en";
type EditorMode = "formatted" | "raw";

function parseLegal(value: unknown, fallback: LegalMarkdown): LegalMarkdown {
  if (!value || typeof value !== "object") return fallback;
  const record = value as { de?: unknown; en?: unknown };
  return {
    de: typeof record.de === "string" ? record.de : fallback.de,
    en: typeof record.en === "string" ? record.en : fallback.en,
  };
}

function LegalDocumentCard({
  settingKey,
  testId,
  titleKey,
  bodyKey,
  fallback,
}: {
  settingKey: "legal_privacy" | "legal_terms";
  testId: string;
  titleKey: string;
  bodyKey: string;
  fallback: LegalMarkdown;
}) {
  const { t } = useTranslation();
  const settings = useAdminSettings();
  const update = useAdminUpdateSetting();
  const [locale, setLocale] = useState<Locale>("de");
  const [mode, setMode] = useState<EditorMode>("formatted");
  const [draft, setDraft] = useState<LegalMarkdown | null>(null);
  const rawRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (mode !== "raw") return;
    const el = rawRef.current;
    if (!el) return;
    el.scrollTop = 0;
    el.setSelectionRange(0, 0);
  }, [mode, locale]);

  const row = (settings.data ?? []).find((item) => item.key === settingKey);
  const saved = parseLegal(row?.value, fallback);
  const value = draft ?? saved;

  function save() {
    update.mutate(
      { key: settingKey, value },
      {
        onSuccess: () => {
          toast.success(t("admin.settingsSaved"));
          setDraft(null);
        },
        onError: (error) => toast.error(describeError(error)),
      },
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
      <h3 className="text-base font-medium">{t(titleKey)}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{t(bodyKey)}</p>
      {row?.updatedAt && (
        <p className="mt-1 text-xs text-muted-foreground">
          {t("admin.settingsUpdated")}: {new Date(row.updatedAt).toLocaleString()}
        </p>
      )}
      <Tabs value={locale} onValueChange={(next) => setLocale(next as Locale)} className="mt-4">
        <TabsList>
          <TabsTrigger value="de" data-testid={`${testId}-locale-de`}>
            {t("legal.localeDe")}
          </TabsTrigger>
          <TabsTrigger value="en" data-testid={`${testId}-locale-en`}>
            {t("legal.localeEn")}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <Tabs value={mode} onValueChange={(next) => setMode(next as EditorMode)} className="mt-3">
        <TabsList>
          <TabsTrigger value="formatted" data-testid={`${testId}-mode-formatted`}>
            {t("admin.legalEditorFormatted")}
          </TabsTrigger>
          <TabsTrigger value="raw" data-testid={`${testId}-mode-raw`}>
            {t("admin.legalEditorRaw")}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <p className="mt-2 text-xs text-muted-foreground">{t("admin.legalEditorRawHint")}</p>
      <div className="mt-3" data-testid={`${testId}-editor-${locale}`}>
        {mode === "raw" ? (
          <Textarea
            ref={rawRef}
            value={value[locale]}
            onChange={(event) => setDraft({ ...value, [locale]: event.target.value })}
            className="block h-[28rem] min-h-48 resize-y font-mono text-sm leading-6"
            aria-label={t("admin.legalEditorRaw")}
            data-testid={`${testId}-raw-${locale}`}
          />
        ) : (
          <Suspense fallback={<Skeleton className="h-48 w-full rounded-md" />}>
            <Editor
              key={`${settingKey}-${locale}-${mode}`}
              markdown={value[locale]}
              onChange={(markdown) => setDraft({ ...value, [locale]: markdown })}
            />
          </Suspense>
        )}
      </div>
      <Button
        type="button"
        className="mt-3"
        disabled={update.isPending}
        onClick={save}
        data-testid={`${testId}-save`}
      >
        {t("common.save")}
      </Button>
    </div>
  );
}

export function LegalSettingsSection() {
  const { t } = useTranslation();
  return (
    <section className="space-y-4" data-testid="settings-legal-section">
      <div>
        <h2 className="text-lg font-medium">{t("admin.settingsLegalSectionTitle")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("admin.settingsLegalSectionBody")}</p>
      </div>
      <LegalDocumentCard
        settingKey="legal_privacy"
        testId="legal-privacy-settings-card"
        titleKey="admin.legalPrivacyTitle"
        bodyKey="admin.legalPrivacyBody"
        fallback={DEFAULT_LEGAL_PRIVACY}
      />
      <LegalDocumentCard
        settingKey="legal_terms"
        testId="legal-terms-settings-card"
        titleKey="admin.legalTermsTitle"
        bodyKey="admin.legalTermsBody"
        fallback={DEFAULT_LEGAL_TERMS}
      />
    </section>
  );
}
